import type { TesterPresenceItem } from '@runhuman/shared';
import { getAliasInitial, isOffScheduleAndPresent, MENU_GROUP_LABELS } from '@runhuman/shared';
import type { TesterMenuGroup } from '@runhuman/shared';
import { apiPrefix } from '../../api-prefix';
import { formatBackAtTime } from '../../format-time';
import { TesterDeviceIcons } from './TesterDeviceIcons';
import { FloorManagerBadge } from '../FloorManagerBadge';

interface Props {
  readonly tester: TesterPresenceItem;
  readonly group: TesterMenuGroup;
  /**
   * When set, the row becomes clickable and clicking it invokes the callback
   * with the row's `tester`. Threaded down from `<TesterView>` and only
   * provided for admin viewers (#3967) — non-admins receive `undefined` and
   * the row stays inert.
   */
  readonly onClick?: (tester: TesterPresenceItem) => void;
}

/**
 * Single tester row in the Activity presence roster.
 *
 * Mirrors the web `<TeamMemberCard>` data model 1:1 — same fields, same
 * derivations, same visual hierarchy (avatar disc with status-colored ring,
 * floor-manager star, alias, status label). Cosmetic differences only:
 * Activity uses CSS variables instead of the web theme object, and the
 * iframe layout is denser, so labels live in a single line below the alias
 * rather than the web's two-line stack.
 *
 * On-break per-row override (#4062): when the stored availability is
 * `on_break`, the row keeps the bucket `getPresenceStatus` chose (typically
 * `away`, per #3667 — the row still renders inside the Away section), but
 * the dot color and status label flip to a distinct "On break" treatment so
 * admins can spot which Away testers are on an approved break vs. away for
 * another reason. This is intentionally a per-row override, not a new menu
 * group: #3667 collapsed "On break" out of the top-level groups and we don't
 * reintroduce it.
 *
 * Avatar fallback: when `avatarUrl` is null we render an initials disc
 * driven by `getAliasInitial()` from shared. The web does the same — the
 * iframe just couldn't show a face before because the previous version of
 * this row only rendered an `<img>` and skipped null entirely.
 *
 * Avatar image source: the iframe runs on `*.discordsays.com` with a strict
 * CSP that blocks `<img>` requests to upstream avatar hosts (cdn.discordapp.com,
 * *.supabase.co, …). We address avatars by tester id through the same-origin
 * `/api/avatars/tester/:testerId` proxy — the API resolves the stored URL,
 * pipes the bytes back, and the iframe loads them through `/.proxy/api/...`
 * like every other API call. Fixed in #3918.
 */
export function PresenceMemberRow({ tester, group, onClick }: Props) {
  const dimmed = isOffScheduleAndPresent(tester);
  const initial = getAliasInitial(tester.alias);
  const interactive = Boolean(onClick);
  const onBreak = tester.availability === 'on_break';

  const handleClick = () => onClick?.(tester);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(tester);
    }
  };

  // The status modifier drives the row's color theme (`--status-color` /
  // `--avatar-ring`) and the dot color. The on-break override applies on top
  // of the bucket, so the row still appears in the bucket's section but
  // visually flips to the on-break hue.
  const statusModifier = onBreak ? 'on-break' : group;
  const statusLabel = onBreak ? 'On break' : MENU_GROUP_LABELS[group];
  // "back at H:MM" chip surfaces the approved break's end-time alongside the
  // existing "On break" label so admins glancing at the roster can plan around
  // it without clicking through to the schedule (#4166). Decoupled from the
  // `availability` gate so we render nothing during the brief window between a
  // break ending / being returned and the periodic sweep clearing the stored
  // `availability='on_break'` value.
  const backAtChip =
    onBreak && tester.onBreakUntil ? formatBackAtTime(tester.onBreakUntil) : null;

  return (
    <li
      className={`presence-row presence-row--${statusModifier}${dimmed ? ' presence-row--dimmed' : ''}${interactive ? ' presence-row--interactive' : ''}`}
      data-tester-id={tester.id}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
    >
      <div className="presence-row__avatar">
        {tester.avatarUrl ? (
          <img
            src={`${apiPrefix}/avatars/tester/${tester.id}`}
            alt=""
            className="presence-row__avatar-img"
          />
        ) : (
          <span className="presence-row__avatar-initial" aria-hidden="true">
            {initial}
          </span>
        )}
      </div>
      <div className="presence-row__text">
        <div className="presence-row__name-row">
          {tester.isFloorManager && <FloorManagerBadge variant="compact" />}
          <span className="presence-row__alias">{tester.alias}</span>
          <TesterDeviceIcons devices={tester.devices} />
        </div>
        <div className="presence-row__status" title={tester.activeJob ? tester.activeJob.url : undefined}>
          <span className={`presence-row__dot presence-row__dot--${statusModifier}`} aria-hidden="true" />
          <span className="presence-row__status-label">{statusLabel}</span>
          {backAtChip && (
            <span className="presence-row__status-detail">
              · ☕ back at {backAtChip}
            </span>
          )}
          {tester.activeJob && tester.activeJob.project && (
            <span className="presence-row__status-detail">
              · {tester.activeJob.project.organizationName}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
